import { css } from 'styled-components';
import { transparentize } from 'utils/color';
import media from './media';

const defaultMainFont = 'PT Serif, Serif';
const defaultSecondaryFont = 'Arial, Sans-Serif';

export const fontFamilyWithFallback = (fontFamily, fallback) =>
  `${fontFamily || 'Arial'}, ${fallback}`;

export const mainFont = css`
  font-family: ${props =>
    fontFamilyWithFallback(props.theme.fonts.mainFont.fontFamily, defaultMainFont)};
`;

export const secondaryFont = css`
  font-family: ${props =>
    fontFamilyWithFallback(props.theme.fonts.secondaryFont.fontFamily, defaultSecondaryFont)};
`;

const styledColors = (fontStyles, isInverted, InvertedColor) => css`
  color: ${isInverted ? InvertedColor : fontStyles.color};
`;

const styledFonts = fontStyles => css`
  font-family: ${fontFamilyWithFallback(fontStyles.fontFamily, defaultMainFont)};
  font-size: ${fontStyles.size}px;
  font-style: ${fontStyles.fontStyle};
  font-weight: ${fontStyles.fontWeight};
  text-decoration: ${fontStyles.textDecoration};
  background-color: ${fontStyles.textBackgroundColor};
`;

const mediaFontSize = (fontSize, percentage) => css`
  font-size: ${fontSize * percentage}px;
`;

export const bold = css`
  font-weight: 600;
`;

export const mainFontH1 = css`
  ${props => styledFonts(props.theme.fonts.heading1)};
  line-height: 1.02;
  ${props =>
    styledColors(
      props.theme.fonts.heading1,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  ${media.tablet`
    ${props => mediaFontSize(props.theme.fonts.heading1.size, 0.92)};
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.heading1.size, 0.6)};
    line-height: 1.13;
  `};
`;

export const mainFontH2 = css`
  ${props => styledFonts(props.theme.fonts.heading2)};
  line-height: 1.1;
  ${props =>
    styledColors(
      props.theme.fonts.heading2,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  ${media.tablet`
    ${props => mediaFontSize(props.theme.fonts.heading2.size, 0.9)};
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.heading2.size, 0.65)};
  `};
`;

export const mainFontH3 = css`
  ${props => styledFonts(props.theme.fonts.heading3)};
  line-height: 1.39;
  ${props =>
    styledColors(
      props.theme.fonts.heading3,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  ${media.xlarge`
    ${props => mediaFontSize(props.theme.fonts.heading3.size, 0.93)};
    line-height: 1.83;
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.heading3.size, 0.77)};
    line-height: 2;
  `};
`;

export const mainFontH4 = css`
  ${props => styledFonts(props.theme.fonts.heading4)};
  line-height: 2;
  ${props =>
    styledColors(
      props.theme.fonts.heading4,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  ${media.xlarge`
    ${props => mediaFontSize(props.theme.fonts.heading4.size, 0.91)};
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.heading4.size, 0.82)};
  `};
`;

export const mainFontText = css`
  ${mainFont};
  font-size: 18px;
  ${bold};
  line-height: 36px;
  ${media.xlarge`
    font-size: 16px;,
    line-height: 32px;
  `};
  ${media.tablet`
    font-size: 15px;
    line-height: 30px;
  `};
  ${media.mobile`
    font-size: 14px;
    line-height: 28px;
  `};
`;

export const mainFontSmallText = css`
  ${mainFont};
  font-size: 14px;
  ${bold};
  line-height: 28px;
  ${media.xlarge`
    font-size: 12px;
  `};
`;

export const secondaryFontH1 = css`
  ${secondaryFont};
  font-size: 36px;
  font-weight: 100;
  line-height: 54px;
  ${media.mobile`
    font-size: 32px;
    line-height: 48px;
  `};
`;

export const secondaryFontText = css`
  ${props => styledFonts(props.theme.fonts.normal)};
  line-height: 2;
  ${props =>
    styledColors(
      props.theme.fonts.normal,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  ${media.xlarge`
    ${props => mediaFontSize(props.theme.fonts.normal.size, 0.88)};
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.normal.size, 0.82)};
  `};
`;

export const secondaryFontSmallText = css`
  ${secondaryFont};
  font-size: 14px;
  line-height: 28px;
  ${media.xlarge`
    font-size: 12px;
  `};
  ${media.mobile`
    font-size: 11px;
    line-height: 26px;
  `};
`;

export const quote = css`
  ${props => styledFonts(props.theme.fonts.quote)};
  line-height: 1.46;
  ${props =>
    styledColors(
      props.theme.fonts.quote,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  ${media.xlarge`
    ${props => mediaFontSize(props.theme.fonts.quote.size, 0.91)};
    line-height: 1.8;
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.quote.size, 0.73)};
    line-height: 2;
  `};
`;

export const highlighted = css`
  ${props => styledFonts(props.theme.fonts.highlighted)};
  line-height: 2;
  ${props =>
    styledColors(
      props.theme.fonts.highlighted,
      props.inverted,
      props.theme.colors.coverBgOverlayTextColor
    )};
  background-color: ${props => props.theme.fonts.highlighted.textBackgroundColor};
  ${media.xlarge`
    ${props => mediaFontSize(props.theme.fonts.highlighted.size, 0.88)};
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.highlighted.size, 0.82)};
  `};
`;

export const links = css`
  ${props => styledFonts(props.theme.fonts.links)};
  line-height: 2;
  color: ${props =>
    props.inverted
      ? transparentize(props.theme.colors.coverBgOverlayTextColor, 0.7)
      : transparentize(props.theme.fonts.links.color, 0.7)};
  ${media.xlarge`
    ${props => mediaFontSize(props.theme.fonts.links.size, 0.88)};
  `};
  ${media.mobile`
    ${props => mediaFontSize(props.theme.fonts.links.size, 0.82)};
  `};

  &:hover {
    ${props =>
      styledColors(
        props.theme.fonts.links,
        props.inverted,
        props.theme.colors.coverBgOverlayTextColor
      )};
  }
`;
